import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Activity, User, Clock, FileText, Image, Briefcase, 
  Settings, Shield, LogIn, LogOut, Key
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    try {
      const params = filter !== 'all' ? `?resource_type=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/admin/audit-logs${params}`, {
        headers: getAuthHeaders()
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action, resourceType) => {
    if (action === 'login') return <LogIn className="w-5 h-5 text-green-600" />;
    if (action === 'logout') return <LogOut className="w-5 h-5 text-gray-600" />;
    if (action === 'password_change' || action === 'password_reset') return <Key className="w-5 h-5 text-orange-600" />;
    
    switch (resourceType) {
      case 'job': return <Briefcase className="w-5 h-5 text-blue-600" />;
      case 'gallery': return <Image className="w-5 h-5 text-purple-600" />;
      case 'application': return <FileText className="w-5 h-5 text-amber-600" />;
      case 'user': return <User className="w-5 h-5 text-red-600" />;
      case 'settings': return <Settings className="w-5 h-5 text-gray-600" />;
      default: return <Activity className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      login: 'bg-emerald-100 text-emerald-800',
      logout: 'bg-gray-100 text-gray-800',
      password_change: 'bg-orange-100 text-orange-800',
      password_reset: 'bg-orange-100 text-orange-800'
    };
    return (
      <Badge className={styles[action] || 'bg-gray-100 text-gray-800'}>
        {action.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-audit-logs">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Audit Logs</h2>
          <p className="text-gray-600 mt-2">Track all admin actions and changes ({logs.length} entries)</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {['all', 'auth', 'job', 'application', 'gallery', 'user', 'settings'].map(type => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === type 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {logs.length === 0 ? (
        <Card className="p-12 text-center">
          <CardContent>
            <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Audit Logs</h3>
            <p className="text-gray-500">
              {filter === 'all' 
                ? 'No actions have been logged yet.'
                : `No logs found for "${filter}" category.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {logs.map(log => (
            <Card key={log.id} className="hover:shadow-md transition-shadow" data-testid={`log-${log.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    {getActionIcon(log.action, log.resource_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900">{log.username}</span>
                      {getActionBadge(log.action)}
                      <Badge variant="outline" className="text-xs">
                        {log.resource_type}
                      </Badge>
                    </div>
                    <p className="text-gray-700">{log.details || 'No details provided'}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(log.timestamp)}
                      </span>
                      {log.ip_address && (
                        <span className="flex items-center gap-1">
                          <Shield className="w-4 h-4" />
                          IP: {log.ip_address}
                        </span>
                      )}
                      {log.resource_id && (
                        <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                          ID: {log.resource_id.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAuditLogs;
